import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { ConfigurationDetail, ConfigurationStatus } from '@/types';
import { CompletionProgress } from './CompletionProgress';

interface ConfigurationCardProps {
  configuration: ConfigurationDetail;
  /** Progressivo 1-based mostrato come "01.", "02."… (come il legacy). */
  index: number;
  onOpen: (id: number) => void;
  /** Azioni CRUD (Fase B). Se assenti/`editable=false` la card è di sola apertura. */
  onRename?: (id: number, name: string) => void;
  onDuplicate?: (id: number) => void;
  onDelete?: (id: number) => void;
  /** Se false, nasconde le azioni (es. appartamento con una configurazione confermata). */
  editable?: boolean;
  /** Se false, "Duplica" è disabilitato (limite di 4 configurazioni per appartamento raggiunto). */
  canDuplicate?: boolean;
}

// Card della lista "Le tue configurazioni": bordo evidenziato quando la config è confermata.
const CardRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'confirmed',
})<{ confirmed: boolean }>(({ theme, confirmed }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
  height: '100%',
  backgroundColor: theme.palette.background.paper,
  border: `${confirmed ? 2 : 1}px solid`,
  borderColor: confirmed ? theme.palette.primary.main : theme.palette.divider,
  borderRadius: theme.shape.borderRadius,
}));

const STATUS_LABEL: Record<ConfigurationStatus, string> = {
  draft: 'Bozza',
  approved: 'Approvata',
  confirmed: 'Confermata',
};

/** Data dell'ultimo salvataggio (fallback: ultima risposta → creazione). */
function lastSavedDate(c: ConfigurationDetail): string | null {
  return c.lastUpdated ?? c.lastAnswerDate ?? c.creationDate ?? null;
}

/** Formatta una data ISO in it-IT (gg/mm/aaaa hh:mm), o "—" se assente/non valida. */
function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

export function ConfigurationCard({
  configuration,
  index,
  onOpen,
  onRename,
  onDuplicate,
  onDelete,
  editable = true,
  canDuplicate = true,
}: ConfigurationCardProps) {
  const confirmed = configuration.status === 'confirmed';
  const name = configuration.name || `Configurazione #${configuration.id}`;

  const showActions = editable && (onRename != null || onDuplicate != null || onDelete != null);

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [draftName, setDraftName] = useState(name);

  const closeMenu = (): void => setMenuAnchor(null);

  const startRenaming = (): void => {
    setDraftName(configuration.name);
    setIsRenaming(true);
    closeMenu();
  };

  const cancelRenaming = (): void => {
    setIsRenaming(false);
    setDraftName(configuration.name);
  };

  const saveRenaming = (): void => {
    const trimmed = draftName.trim();
    setIsRenaming(false);
    if (trimmed && trimmed !== configuration.name) {
      onRename?.(configuration.id, trimmed);
    }
  };

  return (
    <CardRoot confirmed={confirmed}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary', minWidth: 0 }}>
          <AccessTimeIcon fontSize="small" aria-hidden="true" />
          <Typography variant="caption" noWrap>
            {formatDate(lastSavedDate(configuration))}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Chip
            size="small"
            label={STATUS_LABEL[configuration.status]}
            color={confirmed ? 'primary' : 'default'}
            variant={confirmed ? 'filled' : 'outlined'}
            {...(confirmed ? { icon: <CheckCircleIcon fontSize="small" /> } : {})}
            sx={{ fontWeight: 500 }}
          />
          {showActions && (
            <IconButton
              size="small"
              aria-label={`Azioni per la configurazione ${name}`}
              onClick={(e) => setMenuAnchor(e.currentTarget)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography variant="h4" component="span" color="primary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {String(index).padStart(2, '0')}.
        </Typography>
        {isRenaming ? (
          <TextField
            autoFocus
            fullWidth
            size="small"
            variant="standard"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveRenaming();
              if (e.key === 'Escape') cancelRenaming();
            }}
            inputProps={{ 'aria-label': 'Nuovo nome configurazione' }}
          />
        ) : (
          <Typography variant="h4" component="span" sx={{ minWidth: 0 }}>
            {name}
          </Typography>
        )}
      </Box>

      {isRenaming && (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="contained" color="primary" onClick={saveRenaming}>
            Salva
          </Button>
          <Button size="small" color="inherit" onClick={cancelRenaming}>
            Annulla
          </Button>
        </Stack>
      )}

      <Box sx={{ mt: 'auto' }}>
        <CompletionProgress configId={configuration.id} />
      </Box>

      <Button
        variant={confirmed ? 'outlined' : 'contained'}
        color="primary"
        onClick={() => onOpen(configuration.id)}
        sx={{ alignSelf: 'flex-start' }}
        aria-label={`Apri la configurazione ${name}`}
      >
        {confirmed ? 'Visualizza' : 'Continua la personalizzazione'}
      </Button>

      <Menu anchorEl={menuAnchor} open={menuAnchor != null} onClose={closeMenu}>
        {onRename != null && (
          <MenuItem onClick={startRenaming}>
            <ListItemIcon>
              <DriveFileRenameOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Rinomina</ListItemText>
          </MenuItem>
        )}
        {onDuplicate != null && (
          <MenuItem
            disabled={!canDuplicate}
            onClick={() => {
              closeMenu();
              onDuplicate(configuration.id);
            }}
          >
            <ListItemIcon>
              <ContentCopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Duplica</ListItemText>
          </MenuItem>
        )}
        {onDelete != null && (
          <MenuItem
            onClick={() => {
              closeMenu();
              onDelete(configuration.id);
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Elimina</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </CardRoot>
  );
}
